/**
 * Project photos in Teslyn Cloud (MB decisions 427-431; docs/specs/cloud/project-photos.md). RLS and the storage
 * policies of `20261021040000_project_photos.sql` decide what each user may read and write; these functions only ask.
 * The client loader also filters on exactly what the client policy allows (shared and live), so Preview as client,
 * which runs with the staff member's wider rights, shows the client's view.
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  photoFileCheck,
  photoLocalPath,
  photoObjectPath,
  PHOTO_BUCKET,
  readPhotoExif,
  type CloudPhoto,
  type PhotoCategory,
  type PhotoRendition,
} from '@teslyn/domain';
import { archiveNow, FunctionArchiveSender, sha256Hex, type ArchiveAck, type ArchiveNow } from '@teslyn/sync';
import { readConfig } from '../config.ts';
import { all, type Res } from './api.ts';
import type { PhotoRenderer } from './photoImages.ts';

/** A photo row as Teslyn Cloud holds it. */
export type PortalPhoto = CloudPhoto & { readonly tenant_id: string };

export type PhotoFileKind = 'photo' | 'photo_display' | 'photo_thumb';

/** Which of a photo's three files Teslyn Cloud has recorded, by photo id. */
export type PhotoFiles = ReadonlyMap<string, ReadonlySet<PhotoFileKind>>;

export const PHOTO_LINK_SECONDS = 300;

/** What a client is given of a row: no uploader, hashes or laptop paths. */
const CLIENT_COLUMNS =
  'id, tenant_id, project_id, file_name, file_ext, size_bytes, width, height, caption, category, taken_local, taken_offset, taken_lat, taken_lon, source, created_at, updated_at, deleted_at, shared_at';

type ClientRow = Omit<PortalPhoto, 'photo_path' | 'sha256' | 'added_by_name' | 'updated_by' | 'shared_by'>;

/** Database and Storage errors in words: the database's own sentence where it gives one. */
export function photoError(e: { readonly message: string; readonly code?: string } | unknown): string {
  if (e && typeof e === 'object' && 'message' in e && typeof e.message === 'string') {
    if (/row-level security|permission denied/i.test(e.message))
      return 'You do not have permission to do this.';
    return e.message;
  }
  return String(e);
}

function mustPhoto<T>(r: Res<T>): T {
  if (r.error) throw new Error(photoError(r.error));
  return r.data as T;
}

/**
 * Live photos of a project. Staff read every live photo; a client (and Preview as client) only the shared ones,
 * read with only the columns a client is shown.
 */
export async function loadPhotos(
  sb: SupabaseClient,
  projectId: string,
  mode: 'staff' | 'client',
): Promise<PortalPhoto[]> {
  if (mode === 'staff') {
    return all<PortalPhoto>((from, to) =>
      sb
        .from('project_photo')
        .select('*')
        .eq('project_id', projectId)
        .is('deleted_at', null)
        .order('id')
        .range(from, to),
    );
  }
  const rows = await all<ClientRow>((from, to) =>
    sb
      .from('project_photo')
      .select(CLIENT_COLUMNS)
      .eq('project_id', projectId)
      .not('shared_at', 'is', null)
      .is('deleted_at', null)
      .order('id')
      .range(from, to),
  );
  return rows.map((r) => ({
    ...r,
    photo_path: '',
    sha256: '',
    added_by_name: '',
    updated_by: null,
    shared_by: null,
  }));
}

/** Staff only: which files of the project's photos are in Teslyn Cloud (project_file). */
export async function loadPhotoFiles(sb: SupabaseClient, projectId: string): Promise<PhotoFiles> {
  const rows = await all<{ record_id: string; kind: PhotoFileKind }>((from, to) =>
    sb
      .from('project_file')
      .select('record_id, kind')
      .eq('project_id', projectId)
      .in('kind', ['photo', 'photo_display', 'photo_thumb'])
      .order('record_id')
      .range(from, to),
  );
  const out = new Map<string, Set<PhotoFileKind>>();
  for (const r of rows) {
    const set = out.get(r.record_id) ?? new Set<PhotoFileKind>();
    set.add(r.kind);
    out.set(r.record_id, set);
  }
  return out;
}

/** The object name of one of a photo's files, or null when the row cannot name one. */
export function photoPath(p: PortalPhoto, rendition: PhotoRendition): string | null {
  try {
    return photoObjectPath(p.tenant_id, p.project_id, p.id, rendition, p.file_ext);
  } catch {
    return null;
  }
}

/** Signed addresses (5 minutes) for these objects, made after the storage policies allow each; failures left out. */
export async function signedPhotoUrls(
  sb: SupabaseClient,
  paths: readonly string[],
): Promise<Map<string, string>> {
  const out = new Map<string, string>();
  if (paths.length === 0) return out;
  const bucket = sb.storage.from(PHOTO_BUCKET);
  if (typeof bucket.createSignedUrls === 'function') {
    const { data, error } = await bucket.createSignedUrls([...paths], PHOTO_LINK_SECONDS);
    if (error) throw new Error(photoError(error));
    for (const d of data ?? []) if (d.path && d.signedUrl && !d.error) out.set(d.path, d.signedUrl);
    return out;
  }
  await Promise.all(
    paths.map(async (p) => {
      const { data } = await bucket.createSignedUrl(p, PHOTO_LINK_SECONDS);
      if (data?.signedUrl) out.set(p, data.signedUrl);
    }),
  );
  return out;
}

/** Where a photo's original is for "Download original" (decision 643); 'unknown' before the migration is applied. */
export type OriginalPlace = 'cloud' | 'nextcloud' | 'not_uploaded' | 'unknown';

/** Staff only: where this photo's original is (public.photo_original_source, as the signed-in person). */
export async function photoOriginalPlace(sb: SupabaseClient, photoId: string): Promise<OriginalPlace> {
  const { data, error } = await sb.rpc('photo_original_source', { p_photo: photoId });
  if (error) return 'unknown';
  const status = (data as { status?: string } | null)?.status;
  return status === 'nextcloud' || status === 'cloud' || status === 'not_uploaded' ? status : 'unknown';
}

/**
 * The original of a photo kept in the organisation's Nextcloud, fetched through Teslyn Cloud (Edge Function
 * photo-originals; the app password never reaches the browser). Errors are the function's own words.
 */
export async function fetchArchivedOriginal(sb: SupabaseClient, photoId: string): Promise<Blob> {
  const { data, error } = await sb.functions.invoke<Blob>('photo-originals', {
    body: { action: 'download', photo_id: photoId },
  });
  if (error) {
    let msg = 'Teslyn Cloud could not fetch the original just now. Try again.';
    const ctx = (error as { context?: unknown }).context;
    if (ctx instanceof Response) {
      try {
        const j = (await ctx.json()) as { error?: string };
        if (j.error) msg = j.error;
      } catch {
        /* keep the words above */
      }
    }
    throw new Error(msg);
  }
  if (!(data instanceof Blob)) throw new Error('Teslyn Cloud did not send the original.');
  return data;
}

/** One signed address (5 minutes); `download` names the saved file. */
export async function signedPhotoUrl(sb: SupabaseClient, path: string, download?: string): Promise<string> {
  const { data, error } = await sb.storage
    .from(PHOTO_BUCKET)
    .createSignedUrl(path, PHOTO_LINK_SECONDS, download ? { download } : undefined);
  if (error || !data) throw new Error(error ? photoError(error) : 'The photo could not be opened.');
  return data.signedUrl;
}

async function updateOne(sb: SupabaseClient, id: string, values: Record<string, unknown>): Promise<void> {
  const res = (await sb.from('project_photo').update(values).eq('id', id).select('id')) as Res<unknown[]>;
  if (mustPhoto(res)?.length === 0) throw new Error('Nothing was changed. You may not have permission.');
}

/**
 * "Share with client" (decision 428). The database refuses unless the user is a manager or office staff and the
 * display size and thumbnail are in Teslyn Cloud, and stamps the time and who shared it.
 */
export function setPhotoShared(sb: SupabaseClient, id: string, shared: boolean): Promise<void> {
  return updateOne(sb, id, { shared_at: shared ? new Date().toISOString() : null });
}

/** Decision 515: any staff member puts a photo forward for sharing, or withdraws it; the database stamps who. */
export function setPhotoPutForward(sb: SupabaseClient, id: string, forward: boolean): Promise<void> {
  return updateOne(sb, id, { share_requested_at: forward ? new Date().toISOString() : null });
}

export function updatePhotoDetails(
  sb: SupabaseClient,
  id: string,
  details: { readonly caption: string; readonly category: PhotoCategory },
): Promise<void> {
  return updateOne(sb, id, { caption: details.caption.trim(), category: details.category });
}

/** Deleted as every synced row is: `deleted_at` (the files are kept, decision 242). */
export function deletePhoto(sb: SupabaseClient, id: string): Promise<void> {
  return updateOne(sb, id, { deleted_at: new Date().toISOString() });
}

// ---------------------------------------------------------------------------------------------------- adding

export type ArchivePhoto = (file: ArchiveNow) => Promise<ArchiveAck>;

/** The file archive (decision 431) through Edge Function archive-upload, as the signed-in user. */
export function functionArchive(sb: SupabaseClient): ArchivePhoto {
  const cfg = readConfig();
  const sender = new FunctionArchiveSender({
    supabaseUrl: cfg.supabaseUrl,
    publishableKey: cfg.supabasePublishableKey,
    token: async () => (await sb.auth.getSession()).data.session?.access_token ?? null,
    signedOut: () => new Error('You are signed out. Sign in again to copy photos to the file archive.'),
  });
  return (file) => archiveNow(file, sender);
}

export interface AddPhotoDeps {
  readonly sb: SupabaseClient;
  readonly renderer: PhotoRenderer;
  readonly archive: ArchivePhoto;
  readonly newId?: () => string;
  readonly now?: () => Date;
}

export interface AddPhotoOptions {
  readonly category: PhotoCategory;
  readonly caption: string;
}

export interface PhotoAdded {
  readonly photoId: string;
  /** Set when the photo is in Teslyn Cloud but not in the file archive. */
  readonly archiveNotice: string | null;
}

export const ARCHIVE_NOT_COPIED = 'The photo is in Teslyn Cloud but was not copied to the file archive';

const pad = (n: number): string => String(n).padStart(2, '0');
const localDay = (d: Date): string => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

async function bytesOf(blob: Blob): Promise<Uint8Array> {
  if (typeof blob.arrayBuffer === 'function') return new Uint8Array(await blob.arrayBuffer());
  return new Uint8Array(
    await new Promise<ArrayBuffer>((ok, fail) => {
      const r = new FileReader();
      r.onload = () => ok(r.result as ArrayBuffer);
      r.onerror = () => fail(r.error ?? new Error('The file could not be read.'));
      r.readAsArrayBuffer(blob);
    }),
  );
}

function archiveNotice(ack: ArchiveAck): string | null {
  switch (ack.outcome) {
    case 'done':
    case 'skipped': // archiving is switched off for the organisation
      return null;
    case 'retry':
    case 'refused':
      return `${ARCHIVE_NOT_COPIED}: ${ack.message}`;
    default:
      return `${ARCHIVE_NOT_COPIED}.`;
  }
}

/**
 * Add one photo as staff in Teslyn Cloud: check it, read its EXIF, draw its two sizes, save the row, store the three
 * files at their canonical names with a project_file record each, then copy the original to the file archive. A
 * photo whose files could not all be stored is deleted again, so no half-added photo stays in the gallery.
 */
export async function addPhoto(
  deps: AddPhotoDeps,
  project: { readonly id: string; readonly tenantId: string },
  file: File,
  opts: AddPhotoOptions,
): Promise<PhotoAdded> {
  const { sb } = deps;
  const given = file.name.split(/[\\/]/).pop() ?? file.name;
  const check = photoFileCheck(given, file.size);
  if (!check.ok) throw new Error(check.message);
  // a bitmap is kept as a PNG, pixel for pixel, under its new name (R144): the browser converts it here
  const original: Blob = check.converted ? await deps.renderer.toPng(file) : file;
  const name = check.converted ? check.converted.fileName : given;
  const bytes = await bytesOf(original);
  const exif = readPhotoExif(bytes);
  const drawn = await deps.renderer.render(file);
  const sha256 = await sha256Hex(bytes);
  const id = (deps.newId ?? (() => globalThis.crypto.randomUUID()))();

  mustPhoto(
    (await sb.from('project_photo').insert({
      id,
      tenant_id: project.tenantId,
      project_id: project.id,
      file_name: name,
      file_ext: check.ext,
      photo_path: '',
      size_bytes: bytes.byteLength,
      sha256,
      width: drawn.width,
      height: drawn.height,
      caption: opts.caption.trim(),
      category: opts.category,
      taken_local: exif.takenLocal,
      taken_offset: exif.takenOffset,
      taken_lat: exif.lat,
      taken_lon: exif.lon,
      source: 'cloud',
    })) as Res<unknown>,
  );

  const files: ReadonlyArray<{
    kind: PhotoFileKind;
    rendition: PhotoRendition;
    body: Blob;
    bytes: Uint8Array;
    fileName: string;
    contentType: string;
  }> = [
    {
      kind: 'photo',
      rendition: 'original',
      body: original,
      bytes,
      fileName: name,
      contentType: check.contentType,
    },
    {
      kind: 'photo_display',
      rendition: 'display',
      body: drawn.display,
      bytes: await bytesOf(drawn.display),
      fileName: photoLocalPath(id, 'display').split('/').pop()!,
      contentType: 'image/jpeg',
    },
    {
      kind: 'photo_thumb',
      rendition: 'thumb',
      body: drawn.thumb,
      bytes: await bytesOf(drawn.thumb),
      fileName: photoLocalPath(id, 'thumb').split('/').pop()!,
      contentType: 'image/jpeg',
    },
  ];
  try {
    for (const f of files) {
      const path = photoObjectPath(project.tenantId, project.id, id, f.rendition, check.ext);
      const up = await sb.storage
        .from(PHOTO_BUCKET)
        .upload(path, f.body, { upsert: true, contentType: f.contentType, cacheControl: '300' });
      if (up.error) throw new Error(photoError(up.error));
      mustPhoto(
        (await sb.from('project_file').upsert(
          {
            tenant_id: project.tenantId,
            project_id: project.id,
            kind: f.kind,
            record_id: id,
            bucket: PHOTO_BUCKET,
            object_path: path,
            file_name: f.fileName,
            content_type: f.contentType,
            size_bytes: f.bytes.byteLength,
            sha256: f.kind === 'photo' ? sha256 : await sha256Hex(f.bytes),
            source: 'portal',
          },
          { onConflict: 'kind,record_id' },
        )) as Res<unknown>,
      );
    }
  } catch (e) {
    await deletePhoto(sb, id).catch(() => undefined);
    throw e;
  }

  let notice: string | null;
  try {
    notice = archiveNotice(
      await deps.archive({
        tenantId: project.tenantId,
        projectId: project.id,
        fileName: name,
        day: localDay((deps.now ?? (() => new Date()))()),
        bytes,
        source: 'import',
      }),
    );
  } catch (e) {
    notice = `${ARCHIVE_NOT_COPIED}: ${e instanceof Error ? e.message : String(e)}`;
  }
  return { photoId: id, archiveNotice: notice };
}
